import { Map } from 'ol';
import { GeoJSONFeature, GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEVICE_LAYER_CLASS,
    DEVICESETS_LAYER_CLASS,
    MAP_TYPES,
} from '../../../constants/map.constants';
import { StackParams } from '../../../models/models';
import {
    buildDeviceLayer,
    buildDevicesetsLayer,
    initMap,
} from '../../../utils/map/map.utils';
import { BoxFullWH } from '../../layout/layout.styles';
import MapActionsSideBar from '../map-actions-side-bar/map-actions-side-bar.component';
import MapInteractionsHandler from '../map-interactions-handler/map-interactions-handler.component';
import { MapContainer } from './map-wrapper.styles';

const MapWrapper = ({
    devicesets,
    device,
    onMapExpandedChange,
    onDevicesetSelected,
}: {
    devicesets: GeoJSONFeatureCollection;
    device: GeoJSONFeature;
    onMapExpandedChange: () => void;
    onDevicesetSelected: (devicesetId: StackParams) => void;
}) => {
    const mapElement = useRef<HTMLDivElement>(null);

    const [map, setMap] = useState<Map>();

    const [mapType, setMapType] = useState<MAP_TYPES>(MAP_TYPES.ROADMAP);

    const reInitPageMap = useCallback(async () => {
        if (mapElement.current?.innerHTML) {
            mapElement.current.innerHTML = '';
        }
        const _map = await initMap(mapElement);
        setMap(_map);
    }, []);

    const handleMapTypeChange = (newMapType: MAP_TYPES) => {
        setMapType(
            mapType === MAP_TYPES.ROADMAP
                ? MAP_TYPES.SATELLITE
                : MAP_TYPES.ROADMAP
        );
    };

    const handleMapZoomChange = (direction: 'in' | 'out') => () => {
        if (map) {
            const view = map.getView();
            const zoom = view.getZoom();
            if (zoom) {
                view.animate({
                    zoom: direction === 'in' ? zoom + 1 : zoom - 1,
                    duration: 1000,
                });
            }
        }
    };

    useEffect(() => {
        reInitPageMap();
    }, [reInitPageMap]);

    useEffect(() => {
        if (map) {
            map.getLayers().forEach((layer) => {
                const layerMapType = layer.getProperties()['mapType'];
                if (layerMapType) {
                    layer.setVisible(layerMapType === mapType);
                }
            });
        }
    }, [map, mapType]);

    useEffect(() => {
        if (map && devicesets) {
            let oldExtent = null;
            map.getLayers().forEach((layer) => {
                if (layer && layer.getClassName() === DEVICESETS_LAYER_CLASS) {
                    oldExtent = (layer as any).getSource()?.getExtent();
                    if (oldExtent) oldExtent = oldExtent.join(',');
                    map.removeLayer(layer);
                }
            });
            map.getLayers().forEach((layer) => {
                if (layer && layer.getClassName() === DEVICE_LAYER_CLASS) {
                    map.removeLayer(layer);
                }
            });
            const devicesetsLayer = buildDevicesetsLayer(devicesets);
            map.addLayer(devicesetsLayer);
            const source = devicesetsLayer.getSource();
            if (source) {
                const features = source.getFeatures();
                if (features.length > 0) {
                    const extent = source.getExtent();
                    const newExtent = extent?.join(',');
                    if (extent && oldExtent !== newExtent) {
                        map.getView().fit(extent, {
                            maxZoom: 18,
                            padding: [100, 50, 50, 50],
                            duration: 1000,
                        });
                    }
                }
            }
        }
    }, [map, devicesets]);

    useEffect(() => {
        if (map && device) {
            let oldExtent = null;
            map.getLayers().forEach((layer) => {
                if (layer && layer.getClassName() === DEVICESETS_LAYER_CLASS) {
                    map.removeLayer(layer);
                }
            });
            map.getLayers().forEach((layer) => {
                if (layer && layer.getClassName() === DEVICE_LAYER_CLASS) {
                    oldExtent = (layer as any).getSource()?.getExtent();
                    if (oldExtent) oldExtent = oldExtent.join(',');
                    map.removeLayer(layer);
                }
            });
            const devicesLayer = buildDeviceLayer(device);
            map.addLayer(devicesLayer);
            const source = devicesLayer.getSource();
            if (source) {
                const features = source.getFeatures();
                if (features.length > 0) {
                    const extent = source.getExtent();
                    const newExtent = extent?.join(',');
                    if (extent && oldExtent !== newExtent) {
                        map.getView().fit(extent, {
                            maxZoom: 18,
                            padding: [100, 50, 50, 50],
                            duration: 1000,
                        });
                    }
                }
            }
        }
    }, [map, device]);

    return (
        <BoxFullWH position={'relative'}>
            <MapContainer ref={mapElement} />
            {map && (
                <MapActionsSideBar
                    mapType={mapType}
                    onExpandButtonClick={onMapExpandedChange}
                    onMapTypeButtonClick={handleMapTypeChange}
                    onMapZoomInButtonClick={handleMapZoomChange('in')}
                    onMapZoomOutButtonClick={handleMapZoomChange('out')}
                />
            )}
            {map && (
                <MapInteractionsHandler
                    map={map}
                    onDevicesetSelected={onDevicesetSelected}
                />
            )}
        </BoxFullWH>
    );
};

export default MapWrapper;
